import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, TrendingUp, TrendingDown, DollarSign, BarChart3, Package, Search, X, Calendar, AlertCircle, Mail, Settings } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('purchases');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  
  // Accountant Pack states
  const [accountantPackMonth, setAccountantPackMonth] = useState('');
  const [accountantPackYear, setAccountantPackYear] = useState(new Date().getFullYear().toString());
  const [accountantPackLoading, setAccountantPackLoading] = useState(false);
  
  // Batch filter states
  const [batchFilterProduct, setBatchFilterProduct] = useState('');
  const [batchFilterBatch, setBatchFilterBatch] = useState('');
  const [products, setProducts] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  
  // Report data
  const [purchaseReport, setPurchaseReport] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [gstReport, setGstReport] = useState(null);
  const [hsnReport, setHsnReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [batchReport, setBatchReport] = useState(null);
  const [ewayReport, setEwayReport] = useState(null);
  const [batchSalesDetail, setBatchSalesDetail] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isBatchDetailOpen, setIsBatchDetailOpen] = useState(false);
  const [batchSearchTerm, setBatchSearchTerm] = useState('');
  
  // E-Way Bill filter
  const [ewayInvoiceFilter, setEwayInvoiceFilter] = useState('');
  
  // Accountant settings
  const [accountantSettings, setAccountantSettings] = useState({ accountant_name: '', accountant_email: '' });
  const [showAccountantSettings, setShowAccountantSettings] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [emailCustomMessage, setEmailCustomMessage] = useState('');

  useEffect(() => {
    fetchFilters();
    fetchAccountantSettings();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [activeTab, startDate, endDate, selectedVendor, selectedCustomer, batchFilterProduct, batchFilterBatch, ewayInvoiceFilter]);

  const fetchFilters = async () => {
    try {
      const [vendorsRes, customersRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/customers`),
        axios.get(`${API_URL}/products`)
      ]);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch filters');
    }
  };

  const fetchAccountantSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings/accountant`);
      setAccountantSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch accountant settings');
    }
  };

  const saveAccountantSettings = async () => {
    try {
      await axios.put(`${API_URL}/settings/accountant`, accountantSettings);
      toast.success('Accountant settings saved');
      setShowAccountantSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      switch (activeTab) {
        case 'purchases':
          if (selectedVendor) params.vendor_id = selectedVendor;
          const purchasesRes = await axios.get(`${API_URL}/reports/purchases`, { params });
          setPurchaseReport(purchasesRes.data);
          break;
        case 'sales':
          if (selectedCustomer) params.customer_id = selectedCustomer;
          const salesRes = await axios.get(`${API_URL}/reports/sales`, { params });
          setSalesReport(salesRes.data);
          break;
        case 'gst':
          const gstRes = await axios.get(`${API_URL}/reports/gst`, { params });
          setGstReport(gstRes.data);
          // Also fetch HSN summary for GST tab
          const hsnRes = await axios.get(`${API_URL}/reports/hsn-summary`, { params });
          setHsnReport(hsnRes.data);
          break;
        case 'profit':
          const profitRes = await axios.get(`${API_URL}/reports/profit`, { params });
          setProfitReport(profitRes.data);
          break;
        case 'batch':
          let batchParams = {};
          if (startDate) batchParams.start_date = startDate;
          if (endDate) batchParams.end_date = endDate;
          if (batchFilterProduct) batchParams.product_id = batchFilterProduct;
          if (batchFilterBatch) batchParams.batch_number = batchFilterBatch;
          const batchRes = await axios.get(`${API_URL}/reports/batch-traceability`, { params: batchParams });
          setBatchReport(batchRes.data);
          // Extract unique batches for filter dropdown
          if (batchRes.data.batches) {
            const batches = [...new Set(batchRes.data.batches.map(b => b.batch_number))];
            setAvailableBatches(batches);
          }
          break;
        case 'eway':
          let ewayParams = {};
          if (startDate) ewayParams.start_date = startDate;
          if (endDate) ewayParams.end_date = endDate;
          if (selectedCustomer && selectedCustomer !== 'all') ewayParams.customer_id = selectedCustomer;
          if (ewayInvoiceFilter) ewayParams.invoice_number = ewayInvoiceFilter;
          const ewayRes = await axios.get(`${API_URL}/reports/eway-bill`, { params: ewayParams });
          setEwayReport(ewayRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportType) => {
    try {
      let params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(`${API_URL}/reports/export/${reportType}`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleEwayExport = async (format = 'excel') => {
    try {
      let params = { format };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedCustomer && selectedCustomer !== 'all') params.customer_id = selectedCustomer;
      
      const response = await axios.get(`${API_URL}/reports/eway-bill/export`, {
        params,
        responseType: 'blob'
      });
      
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EWay_Bill_Report.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('E-Way Bill report exported successfully');
    } catch (error) {
      toast.error('Failed to export E-Way Bill report');
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedVendor('');
    setSelectedCustomer('');
    setActiveQuickFilter('');
    setShowCustomRange(false);
  };

  // Quick filter date calculations
  const getDateRange = (filter) => {
    const today = new Date();
    const formatDateStr = (date) => date.toISOString().split('T')[0];
    
    switch (filter) {
      case 'today':
        return { start: formatDateStr(today), end: formatDateStr(today) };
      
      case 'week': {
        const startOfWeek = new Date(today);
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday as start
        startOfWeek.setDate(diff);
        return { start: formatDateStr(startOfWeek), end: formatDateStr(today) };
      }
      
      case 'month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: formatDateStr(startOfMonth), end: formatDateStr(today) };
      }
      
      case 'year': {
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return { start: formatDateStr(startOfYear), end: formatDateStr(today) };
      }
      
      default:
        return { start: '', end: '' };
    }
  };

  const handleQuickFilter = (filter) => {
    if (filter === 'custom') {
      setActiveQuickFilter('custom');
      setShowCustomRange(true);
      return;
    }
    
    const { start, end } = getDateRange(filter);
    setStartDate(start);
    setEndDate(end);
    setActiveQuickFilter(filter);
    setShowCustomRange(false);
  };

  const handleDownloadAccountantPack = async () => {
    if (!accountantPackMonth || !accountantPackYear) {
      toast.error('Please select month and year');
      return;
    }
    
    setAccountantPackLoading(true);
    try {
      const year = parseInt(accountantPackYear);
      const month = parseInt(accountantPackMonth);
      
      // Calculate start and end dates for the selected month
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      const response = await axios.get(`${API_URL}/reports/accountant-pack`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });
      link.setAttribute('download', `Accountant_Pack_${monthName}_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Accountant Pack downloaded successfully');
    } catch (error) {
      toast.error('Failed to download Accountant Pack');
    } finally {
      setAccountantPackLoading(false);
    }
  };

  const handleSendToAccountant = async () => {
    if (!accountantPackMonth || !accountantPackYear) {
      toast.error('Please select month and year first');
      return;
    }
    
    if (!accountantSettings.accountant_email) {
      toast.error('Please configure accountant email in Settings first');
      setShowAccountantSettings(true);
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/reports/send-to-accountant`, {
        month: parseInt(accountantPackMonth),
        year: parseInt(accountantPackYear),
        custom_message: emailCustomMessage || null
      });
      
      toast.success(response.data.message || 'Report sent successfully!');
      setShowSendEmailDialog(false);
      setEmailCustomMessage('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      
      // Provide helpful guidance based on error type
      if (errorMsg.includes('API key is invalid') || errorMsg.includes('Resend API')) {
        toast.error('Email service not configured');
        toast.info('Go to Settings → Email tab to add your Resend API key', { duration: 6000 });
      } else if (errorMsg.includes('Accountant email not configured')) {
        toast.error('Accountant email not set');
        setShowAccountantSettings(true);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleBatchClick = async (batch) => {
    setSelectedBatch(batch);
    try {
      const response = await axios.get(
        `${API_URL}/reports/batch-traceability/${encodeURIComponent(batch.product_id)}/${encodeURIComponent(batch.batch_number)}`
      );
      setBatchSalesDetail(response.data);
      setIsBatchDetailOpen(true);
    } catch (error) {
      toast.error('Failed to fetch batch details');
    }
  };

  const handleExportBatchTraceability = async () => {
    try {
      let params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (batchFilterProduct) params.product_id = batchFilterProduct;
      if (batchFilterBatch) params.batch_number = batchFilterBatch;
      
      const response = await axios.get(`${API_URL}/reports/batch-traceability/export`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'batch_traceability_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Batch traceability report exported successfully');
    } catch (error) {
      toast.error('Failed to export batch traceability report');
    }
  };

  const clearBatchFilters = () => {
    setBatchFilterProduct('');
    setBatchFilterBatch('');
    setBatchSearchTerm('');
  };

  const filteredBatches = batchReport?.batches?.filter(batch => 
    batch.product_name.toLowerCase().includes(batchSearchTerm.toLowerCase()) ||
    batch.batch_number.toLowerCase().includes(batchSearchTerm.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Reports</h1>
        <p className="text-slate-500 text-sm mt-1">View and export business reports</p>
      </div>

      {/* Monthly Accountant Pack */}
      <Card className="border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Monthly Accountant Pack</h3>
                <p className="text-xs text-slate-500">Export all financial data for your accountant</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={accountantPackMonth} onValueChange={setAccountantPackMonth}>
                <SelectTrigger className="w-36 bg-white" data-testid="accountant-pack-month">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">January</SelectItem>
                  <SelectItem value="2">February</SelectItem>
                  <SelectItem value="3">March</SelectItem>
                  <SelectItem value="4">April</SelectItem>
                  <SelectItem value="5">May</SelectItem>
                  <SelectItem value="6">June</SelectItem>
                  <SelectItem value="7">July</SelectItem>
                  <SelectItem value="8">August</SelectItem>
                  <SelectItem value="9">September</SelectItem>
                  <SelectItem value="10">October</SelectItem>
                  <SelectItem value="11">November</SelectItem>
                  <SelectItem value="12">December</SelectItem>
                </SelectContent>
              </Select>
              <Select value={accountantPackYear} onValueChange={setAccountantPackYear}>
                <SelectTrigger className="w-28 bg-white" data-testid="accountant-pack-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleDownloadAccountantPack}
                disabled={accountantPackLoading || !accountantPackMonth}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="download-accountant-pack"
              >
                {accountantPackLoading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Pack
                  </>
                )}
              </Button>
              <Button 
                onClick={() => setShowSendEmailDialog(true)}
                disabled={!accountantPackMonth || sendingEmail}
                variant="outline"
                className="border-teal-600 text-teal-700 hover:bg-teal-50"
                data-testid="send-to-accountant-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send to Accountant
              </Button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-teal-200 flex flex-wrap justify-between items-center">
            <p className="text-xs text-slate-600">
              <span className="font-medium">Includes:</span> Sales Register • Purchase Register • GST Summary • HSN Summary • E-Way Bill Data • Invoice List
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowAccountantSettings(true)}
              className="text-teal-700 hover:text-teal-800"
            >
              <Settings className="w-4 h-4 mr-1" />
              Accountant Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accountant Settings Dialog */}
      <Dialog open={showAccountantSettings} onOpenChange={setShowAccountantSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Accountant Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accountant_name">Accountant Name</Label>
              <Input
                id="accountant_name"
                value={accountantSettings.accountant_name}
                onChange={(e) => setAccountantSettings({...accountantSettings, accountant_name: e.target.value})}
                placeholder="Enter accountant name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accountant_email">Accountant Email</Label>
              <Input
                id="accountant_email"
                type="email"
                value={accountantSettings.accountant_email}
                onChange={(e) => setAccountantSettings({...accountantSettings, accountant_email: e.target.value})}
                placeholder="accountant@example.com"
              />
            </div>
            <p className="text-xs text-slate-500">
              These details will be used for the "Send to Accountant" feature.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountantSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveAccountantSettings} className="bg-teal-600 hover:bg-teal-700">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Accountant Dialog */}
      <Dialog open={showSendEmailDialog} onOpenChange={setShowSendEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Send Report to Accountant
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-sm text-slate-700">
                <span className="font-medium">Month:</span>{' '}
                {accountantPackMonth ? new Date(2026, parseInt(accountantPackMonth) - 1).toLocaleString('default', { month: 'long' }) : '-'}{' '}
                {accountantPackYear}
              </p>
              <p className="text-sm text-slate-700">
                <span className="font-medium">Recipient:</span>{' '}
                {accountantSettings.accountant_email || <span className="text-amber-600">Not configured</span>}
              </p>
            </div>
            
            {!accountantSettings.accountant_email && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  Please configure the accountant email first by clicking "Accountant Settings".
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="custom_message">Custom Message (Optional)</Label>
              <textarea
                id="custom_message"
                value={emailCustomMessage}
                onChange={(e) => setEmailCustomMessage(e.target.value)}
                placeholder="Add a personal note to include in the email..."
                className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendToAccountant} 
              disabled={sendingEmail || !accountantSettings.accountant_email}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4 space-y-4">
          {/* Quick Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500 mr-1">Quick Filters:</span>
            <Button
              variant={activeQuickFilter === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('today')}
              className={activeQuickFilter === 'today' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="filter-today"
            >
              Today
            </Button>
            <Button
              variant={activeQuickFilter === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('week')}
              className={activeQuickFilter === 'week' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="filter-week"
            >
              This Week
            </Button>
            <Button
              variant={activeQuickFilter === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('month')}
              className={activeQuickFilter === 'month' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="filter-month"
            >
              This Month
            </Button>
            <Button
              variant={activeQuickFilter === 'year' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('year')}
              className={activeQuickFilter === 'year' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="filter-year"
            >
              This Year
            </Button>
            <Button
              variant={activeQuickFilter === 'custom' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter('custom')}
              className={activeQuickFilter === 'custom' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              data-testid="filter-custom"
            >
              <Calendar className="w-3 h-3 mr-1" />
              Custom Range
            </Button>
            {(startDate || endDate || selectedVendor || selectedCustomer) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Custom Date Range & Additional Filters */}
          {(showCustomRange || activeQuickFilter === 'custom') && (
            <div className="flex flex-wrap items-end gap-4 pt-2 border-t border-slate-100">
              <div className="space-y-1">
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                  data-testid="report-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                  data-testid="report-end-date"
                />
              </div>
              {activeTab === 'purchases' && (
                <div className="space-y-1">
                  <Label className="text-xs">Vendor</Label>
                  <Select value={selectedVendor || 'all'} onValueChange={(val) => setSelectedVendor(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-48" data-testid="report-vendor-select">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {activeTab === 'sales' && (
                <div className="space-y-1">
                  <Label className="text-xs">Customer</Label>
                  <Select value={selectedCustomer || 'all'} onValueChange={(val) => setSelectedCustomer(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-48" data-testid="report-customer-select">
                      <SelectValue placeholder="All Customers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          <TabsTrigger value="purchases" data-testid="tab-purchases">Purchases</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST</TabsTrigger>
          <TabsTrigger value="profit" data-testid="tab-profit">Profit</TabsTrigger>
          <TabsTrigger value="batch" data-testid="tab-batch">Batch</TabsTrigger>
          <TabsTrigger value="eway" data-testid="tab-eway">E-Way Bill</TabsTrigger>
        </TabsList>

        {/* Purchase Report */}
        <TabsContent value="purchases" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Purchase Report</h2>
            <Button variant="outline" onClick={() => handleExport('purchases')} data-testid="export-purchases">
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>
          
          {purchaseReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Taxable</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(purchaseReport.summary.total_taxable)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total GST (Input)</p>
                    <p className="text-xl font-bold text-emerald-600 tabular-nums mt-1">
                      {formatCurrency(purchaseReport.summary.total_gst)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Amount</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(purchaseReport.summary.total_amount)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Data Table */}
              <Card className="border border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Vendor</TableHead>
                          <TableHead className="text-xs">Invoice #</TableHead>
                          <TableHead className="text-xs">Product</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Taxable</TableHead>
                          <TableHead className="text-xs text-right">GST</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {purchaseReport.purchases.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="text-sm">{formatDate(p.purchase_date)}</TableCell>
                            <TableCell className="text-sm">{p.vendor_name}</TableCell>
                            <TableCell className="text-sm">{p.vendor_invoice_number}</TableCell>
                            <TableCell className="text-sm">{p.product_name}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{p.quantity}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(p.taxable_amount)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(p.gst_amount)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums font-medium">{formatCurrency(p.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Sales Report */}
        <TabsContent value="sales" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Sales Report</h2>
            <Button variant="outline" onClick={() => handleExport('sales')} data-testid="export-sales">
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>
          
          {salesReport && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Taxable</p>
                    <p className="text-lg font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(salesReport.summary.total_taxable)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">CGST</p>
                    <p className="text-lg font-bold text-blue-600 tabular-nums mt-1">
                      {formatCurrency(salesReport.summary.total_cgst)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">SGST</p>
                    <p className="text-lg font-bold text-blue-600 tabular-nums mt-1">
                      {formatCurrency(salesReport.summary.total_sgst)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">IGST</p>
                    <p className="text-lg font-bold text-violet-600 tabular-nums mt-1">
                      {formatCurrency(salesReport.summary.total_igst)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Grand Total</p>
                    <p className="text-lg font-bold text-teal-600 tabular-nums mt-1">
                      {formatCurrency(salesReport.summary.grand_total)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Invoice #</TableHead>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Taxable</TableHead>
                          <TableHead className="text-xs text-right">CGST</TableHead>
                          <TableHead className="text-xs text-right">SGST</TableHead>
                          <TableHead className="text-xs text-right">IGST</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {salesReport.sales.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell className="text-sm font-medium text-teal-600">{s.invoice_number}</TableCell>
                            <TableCell className="text-sm">{formatDate(s.invoice_date)}</TableCell>
                            <TableCell className="text-sm">{s.customer_name}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(s.total_taxable)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(s.total_cgst)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(s.total_sgst)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{formatCurrency(s.total_igst)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums font-medium">{formatCurrency(s.grand_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* GST Report */}
        <TabsContent value="gst" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">GST Report</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExport('hsn')} data-testid="export-hsn">
                <Download className="w-4 h-4 mr-2" />
                Export HSN Summary
              </Button>
              <Button variant="outline" onClick={() => handleExport('gst')} data-testid="export-gst">
                <Download className="w-4 h-4 mr-2" />
                Export GST Summary
              </Button>
            </div>
          </div>
          
          {gstReport && (
            <>
              {/* GST Summary Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="border border-emerald-200 bg-emerald-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4" />
                      Input GST (Credit)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-emerald-700 tabular-nums">
                      {formatCurrency(gstReport.input_gst)}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">From purchases</p>
                  </CardContent>
                </Card>

                <Card className="border border-blue-200 bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Output GST (Liability)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-blue-700 tabular-nums">
                      {formatCurrency(gstReport.output_gst.total)}
                    </p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-blue-600">CGST: {formatCurrency(gstReport.output_gst.cgst)}</p>
                      <p className="text-xs text-blue-600">SGST: {formatCurrency(gstReport.output_gst.sgst)}</p>
                      <p className="text-xs text-blue-600">IGST: {formatCurrency(gstReport.output_gst.igst)}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`border ${gstReport.net_gst_payable >= 0 ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-sm font-medium flex items-center gap-2 ${gstReport.net_gst_payable >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                      <DollarSign className="w-4 h-4" />
                      Net GST Payable
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-3xl font-bold tabular-nums ${gstReport.net_gst_payable >= 0 ? 'text-amber-700' : 'text-red-700'}`}>
                      {formatCurrency(gstReport.net_gst_payable)}
                    </p>
                    <p className={`text-xs mt-1 ${gstReport.net_gst_payable >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {gstReport.net_gst_payable >= 0 ? 'Amount to be paid' : 'Credit available'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* HSN Summary Section */}
              {hsnReport && (
                <Card className="border border-slate-200">
                  <CardHeader className="pb-2 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-teal-600" />
                      HSN Summary (for GST Returns)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* HSN Totals */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-4 bg-slate-50 border-b border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Total Qty</p>
                        <p className="text-base font-bold text-slate-900 tabular-nums">{hsnReport.totals.total_quantity}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Taxable Value</p>
                        <p className="text-base font-bold text-slate-900 tabular-nums">{formatCurrency(hsnReport.totals.total_taxable)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">CGST</p>
                        <p className="text-base font-bold text-blue-600 tabular-nums">{formatCurrency(hsnReport.totals.total_cgst)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">SGST</p>
                        <p className="text-base font-bold text-blue-600 tabular-nums">{formatCurrency(hsnReport.totals.total_sgst)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Total GST</p>
                        <p className="text-base font-bold text-teal-600 tabular-nums">{formatCurrency(hsnReport.totals.total_gst)}</p>
                      </div>
                    </div>
                    
                    {/* HSN Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">HSN Code</TableHead>
                            <TableHead className="text-xs">Product Description</TableHead>
                            <TableHead className="text-xs text-right">Total Qty</TableHead>
                            <TableHead className="text-xs text-right">Taxable Value</TableHead>
                            <TableHead className="text-xs text-right">CGST</TableHead>
                            <TableHead className="text-xs text-right">SGST</TableHead>
                            <TableHead className="text-xs text-right">IGST</TableHead>
                            <TableHead className="text-xs text-right">Total GST</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {hsnReport.hsn_summary.length > 0 ? (
                            hsnReport.hsn_summary.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-sm font-medium text-teal-600">{item.hsn_code}</TableCell>
                                <TableCell className="text-sm">{item.product_description}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums">{item.total_quantity}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.taxable_value)}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.cgst)}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.sgst)}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.igst)}</TableCell>
                                <TableCell className="text-sm text-right tabular-nums font-medium">{formatCurrency(item.total_gst)}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                                No HSN data available for the selected period
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Profit Report */}
        <TabsContent value="profit" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Profit Report</h2>
            <Button variant="outline" onClick={() => handleExport('profit')} data-testid="export-profit">
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>
          
          {profitReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Revenue</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(profitReport.summary.total_revenue)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Cost</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(profitReport.summary.total_cost)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-emerald-600 uppercase">Total Profit</p>
                    <p className="text-xl font-bold text-emerald-700 tabular-nums mt-1">
                      {formatCurrency(profitReport.summary.total_profit)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-teal-200 bg-teal-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-teal-600 uppercase">Profit Margin</p>
                    <p className="text-xl font-bold text-teal-700 tabular-nums mt-1">
                      {profitReport.summary.overall_margin.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Invoice Summary Table */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2 border-b border-slate-100">
                  <CardTitle className="text-sm font-semibold text-slate-700">
                    Invoice Profit Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Invoice #</TableHead>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs text-right">Total Sales</TableHead>
                          <TableHead className="text-xs text-right">Total Cost</TableHead>
                          <TableHead className="text-xs text-right">Profit</TableHead>
                          <TableHead className="text-xs text-right">Margin %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {profitReport.invoices?.length > 0 ? (
                          profitReport.invoices.map((inv, index) => (
                            <TableRow key={index}>
                              <TableCell className="text-sm font-medium text-teal-600">{inv.invoice_number}</TableCell>
                              <TableCell className="text-sm">{formatDate(inv.invoice_date)}</TableCell>
                              <TableCell className="text-sm">{inv.customer_name}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{formatCurrency(inv.total_sales)}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{formatCurrency(inv.total_cost)}</TableCell>
                              <TableCell className={`text-sm text-right tabular-nums font-medium ${inv.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(inv.profit)}
                              </TableCell>
                              <TableCell className={`text-sm text-right tabular-nums ${inv.margin_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {inv.margin_percentage.toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                              No profit data available for the selected period
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Item-level Details Table */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2 border-b border-slate-100">
                  <CardTitle className="text-sm font-semibold text-slate-700">
                    Item-wise Profit Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Invoice #</TableHead>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Product</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Selling Price</TableHead>
                          <TableHead className="text-xs text-right">Cost Price</TableHead>
                          <TableHead className="text-xs text-right">Profit</TableHead>
                          <TableHead className="text-xs text-right">Margin %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {profitReport.details?.length > 0 ? (
                          profitReport.details.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="text-sm text-teal-600">{item.invoice_number}</TableCell>
                              <TableCell className="text-sm">{formatDate(item.invoice_date)}</TableCell>
                              <TableCell className="text-sm">{item.product_name}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{item.quantity}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.selling_price)}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{formatCurrency(item.purchase_price)}</TableCell>
                              <TableCell className={`text-sm text-right tabular-nums font-medium ${item.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(item.profit)}
                              </TableCell>
                              <TableCell className={`text-sm text-right tabular-nums ${item.margin_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {item.margin_percentage.toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                              No item details available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Batch Traceability Report */}
        <TabsContent value="batch" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">Batch Trace Dashboard</h2>
            <Button variant="outline" onClick={handleExportBatchTraceability} data-testid="export-batch">
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>
          
          {/* Filters Card */}
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-4">
                {/* Product Filter */}
                <div className="space-y-1">
                  <Label className="text-xs">Product</Label>
                  <Select value={batchFilterProduct || 'all'} onValueChange={(val) => setBatchFilterProduct(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-52" data-testid="batch-product-filter">
                      <SelectValue placeholder="All Products" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Batch Filter */}
                <div className="space-y-1">
                  <Label className="text-xs">Batch Number</Label>
                  <Select value={batchFilterBatch || 'all'} onValueChange={(val) => setBatchFilterBatch(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-40" data-testid="batch-batch-filter">
                      <SelectValue placeholder="All Batches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Batches</SelectItem>
                      {availableBatches.map((batch) => (
                        <SelectItem key={batch} value={batch}>{batch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Search */}
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <Label className="text-xs">Quick Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search product or batch..."
                      value={batchSearchTerm}
                      onChange={(e) => setBatchSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="batch-search-input"
                    />
                  </div>
                </div>
                
                {/* Clear Filters */}
                {(batchFilterProduct || batchFilterBatch || batchSearchTerm) && (
                  <Button variant="ghost" size="sm" onClick={clearBatchFilters} className="text-slate-500">
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Date Range Info */}
              {(startDate || endDate) && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Date Range: {startDate || 'Start'} to {endDate || 'End'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {batchReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Batches</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {filteredBatches.length}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-emerald-600 uppercase">Total Purchased</p>
                    <p className="text-xl font-bold text-emerald-700 tabular-nums mt-1">
                      {filteredBatches.reduce((sum, b) => sum + b.total_purchased, 0)} units
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-blue-600 uppercase">Total Sold</p>
                    <p className="text-xl font-bold text-blue-700 tabular-nums mt-1">
                      {filteredBatches.reduce((sum, b) => sum + b.total_sold, 0)} units
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-violet-200 bg-violet-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-violet-600 uppercase">Remaining Stock</p>
                    <p className="text-xl font-bold text-violet-700 tabular-nums mt-1">
                      {filteredBatches.reduce((sum, b) => sum + b.remaining_stock, 0)} units
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Product Name</TableHead>
                          <TableHead className="text-xs">Batch Number</TableHead>
                          <TableHead className="text-xs text-right">Total Purchased</TableHead>
                          <TableHead className="text-xs text-right">Total Sold</TableHead>
                          <TableHead className="text-xs text-right">Remaining Stock</TableHead>
                          <TableHead className="text-xs text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBatches.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              {batchSearchTerm ? 'No batches found matching your search' : 'No batch data available'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredBatches.map((batch, index) => (
                            <TableRow key={index} className="hover:bg-slate-50">
                              <TableCell className="text-sm font-medium">{batch.product_name}</TableCell>
                              <TableCell>
                                <button
                                  onClick={() => handleBatchClick(batch)}
                                  className="text-sm text-teal-600 hover:text-teal-700 hover:underline font-medium cursor-pointer"
                                  data-testid={`batch-${batch.batch_number}`}
                                >
                                  {batch.batch_number}
                                </button>
                              </TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{batch.total_purchased}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{batch.total_sold}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums font-medium">
                                {batch.remaining_stock}
                              </TableCell>
                              <TableCell className="text-center">
                                {batch.remaining_stock === 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                    Out of Stock
                                  </span>
                                ) : batch.remaining_stock < 5 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    Low Stock
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    In Stock
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* E-Way Bill Report */}
        <TabsContent value="eway" className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">E-Way Bill Report</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleEwayExport('csv')} data-testid="export-eway-csv">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => handleEwayExport('excel')} data-testid="export-eway-excel">
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>

          {/* E-Way Bill Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Filter by Invoice Number..."
              value={ewayInvoiceFilter}
              onChange={(e) => setEwayInvoiceFilter(e.target.value)}
              className="w-48"
              data-testid="eway-invoice-filter"
            />
            <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
              <SelectTrigger className="w-48" data-testid="eway-customer-filter">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {ewayReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Invoices</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {ewayReport.invoice_count}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Items</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {ewayReport.totals.total_items}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Taxable</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">
                      {formatCurrency(ewayReport.totals.total_taxable)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 uppercase">Total Invoice Value</p>
                    <p className="text-xl font-bold text-teal-600 tabular-nums mt-1">
                      {formatCurrency(ewayReport.totals.total_invoice_value)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* E-Way Bill Data Table */}
              <Card className="border border-slate-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Invoice #</TableHead>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Customer</TableHead>
                          <TableHead className="text-xs">GSTIN</TableHead>
                          <TableHead className="text-xs">Place of Supply</TableHead>
                          <TableHead className="text-xs">HSN</TableHead>
                          <TableHead className="text-xs">Product</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Taxable</TableHead>
                          <TableHead className="text-xs text-right">CGST</TableHead>
                          <TableHead className="text-xs text-right">SGST</TableHead>
                          <TableHead className="text-xs text-right">IGST</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ewayReport.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={13} className="text-center py-8 text-slate-500">
                              No E-Way Bill data available for the selected period
                            </TableCell>
                          </TableRow>
                        ) : (
                          ewayReport.items.map((item, index) => (
                            <TableRow key={index} className="hover:bg-slate-50">
                              <TableCell>
                                <span className="font-mono text-xs text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
                                  {item.invoice_number}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{item.invoice_date}</TableCell>
                              <TableCell className="text-xs font-medium">{item.customer_name}</TableCell>
                              <TableCell>
                                <span className={`text-xs font-mono ${item.gstin_valid ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {item.customer_gstin || 'N/A'}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{item.place_of_supply}</TableCell>
                              <TableCell>
                                <span className={`text-xs font-mono ${item.hsn_valid ? '' : 'text-red-500'}`}>
                                  {item.hsn_code || 'Missing'}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs max-w-32 truncate" title={item.product_description}>
                                {item.product_description}
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{item.quantity}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{formatCurrency(item.taxable_value)}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{formatCurrency(item.cgst)}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{formatCurrency(item.sgst)}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">{formatCurrency(item.igst)}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums font-medium">
                                {formatCurrency(item.total_invoice_value)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Validation Alerts */}
              {ewayReport.items.some(item => !item.gstin_valid || !item.hsn_valid) && (
                <Card className="border border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Data Validation Warnings</p>
                        <ul className="text-sm text-amber-700 mt-1 space-y-1">
                          {ewayReport.items.some(item => !item.gstin_valid) && (
                            <li>• Some invoices have missing or invalid GSTIN</li>
                          )}
                          {ewayReport.items.some(item => !item.hsn_valid) && (
                            <li>• Some products have missing HSN codes</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Batch Sales Detail Dialog */}
      <Dialog open={isBatchDetailOpen} onOpenChange={setIsBatchDetailOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Batch Sales Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBatch && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Product</p>
                    <p className="text-sm font-semibold text-slate-900">{selectedBatch.product_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Batch Number</p>
                    <p className="text-sm font-semibold text-teal-600">{selectedBatch.batch_number}</p>
                  </div>
                </div>
              </div>

              {batchSalesDetail?.sales?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Invoice #</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs text-right">Qty Sold</TableHead>
                        <TableHead className="text-xs">Invoice Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batchSalesDetail.sales.map((sale, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-sm font-medium text-teal-600">{sale.invoice_number}</TableCell>
                          <TableCell className="text-sm">{sale.customer_name}</TableCell>
                          <TableCell className="text-sm text-right tabular-nums font-medium">{sale.quantity_sold}</TableCell>
                          <TableCell className="text-sm">{formatDate(sale.invoice_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>No sales recorded for this batch</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
